/**
 * Gift card numbers out of every log line, trace and error report.
 *
 * ── Why (spec 2026-10-08-gift-cards-industry-grade-design.md §2.2) ──────────
 * OrderUp gift cards carry no PIN (owner decision D1): the number alone spends
 * the balance, in store and — from Phase 2 — online. A number written into a
 * log line or a Sentry event is therefore money left in a third-party system
 * that many people can query. The lookup route alone did it on every call:
 * `GET /api/v1/gift-cards/lookup?cardNumber=<16 digits>` put the full number in
 * the request URL, which the API's access log, Fastify's own request log and
 * every Sentry transaction record verbatim.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 * Any run of 12–19 digits — optionally grouped by single spaces or dashes, the
 * way numbers are printed — becomes `[card…1234]`. The last four survive
 * because they are what an operator matches a complaint against, and the API
 * already shows them (`cardNumberLast4`). 12 is the floor because the shortest
 * card number any program generates is 12+ digits; shorter runs (cents, ids,
 * dates, a giftCardId counter) stay readable.
 *
 * It is applied ONLY on gift-card surfaces (a `/gift-card…` path, a gift-card
 * logger). Masking every 13-digit run everywhere would also eat epoch-ms
 * timestamps and order numbers in unrelated logs, and nobody decided that.
 * PAYMENT card numbers are a narrower shape (Luhn-valid, network prefix) and
 * ARE masked everywhere — see the second half of this file.
 *
 * ── Why this module has NO imports ──────────────────────────────────────────
 * It is consumed by the shared Sentry scrubber (`./sentry`, loaded by config
 * files at process boot), by core's logger, and by the API's log plugin. One
 * copy, three sinks — the same reason `./redaction` is a leaf.
 */

/**
 * 12–19 digits, optionally separated by single spaces/dashes, not inside a
 * longer digit run. The leading boundary is a CAPTURED `(^|non-digit)` rather
 * than a lookbehind: this module ships in browser bundles too, and a
 * lookbehind is a SyntaxError at parse time on Safari before 16.4 — it would
 * take the whole analytics bundle down, not just this rule.
 */
const CARD_NUMBER_RUN = /(^|[^\d])(\d(?:[ -]?\d){11,18})(?!\d)/g;

/** What a masked number becomes. Exported so tests pin the literal. */
export function maskedCardNumber(last4: string): string {
  return `[card…${last4}]`;
}

/**
 * Mask every 12–19 digit run in `text`. Total and non-throwing: it runs inside
 * a `beforeSend` and a logger, where an exception would drop the event or the
 * log line. Anything that is not a string comes back unchanged.
 */
export function redactCardNumberRuns<T>(text: T): T {
  if (typeof text !== 'string' || text.length < 12) return text;
  return text.replace(CARD_NUMBER_RUN, (whole: string, lead: string, run: string) => {
    const digits = run.replace(/[ -]/g, '');
    if (digits.length < 12 || digits.length > 19) return whole;
    return lead + maskedCardNumber(digits.slice(-4));
  }) as unknown as T;
}

/** `/gift-card`, `/gift-cards`, `/gift-card-imports`, `/gift-card-programs`, any prefix. */
const GIFT_CARD_PATH = /\/gift-cards?(?:[-/?#]|$)/i;

/** True when a path or absolute URL is a gift-card surface. */
export function isGiftCardPath(url: unknown): boolean {
  return typeof url === 'string' && GIFT_CARD_PATH.test(url);
}

/** A URL as it may be recorded: card-number runs masked on a gift-card path, else unchanged. */
export function redactGiftCardUrl<T>(url: T): T {
  return isGiftCardPath(url) ? redactCardNumberRuns(url) : url;
}

/**
 * Mask every card-number run in every string reachable from `value`, IN PLACE
 * for objects and arrays (a `beforeSend` mutates and returns its event).
 * Returns the redacted value, which differs from the input only for a string.
 */
export function redactCardNumbersDeep<T>(value: T, depth = 0): T {
  if (depth > 12 || value === null || value === undefined) return value;
  if (typeof value === 'string') return redactCardNumberRuns(value);
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = redactCardNumbersDeep(value[i], depth + 1);
    return value;
  }
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    obj[key] = redactCardNumbersDeep(obj[key], depth + 1);
  }
  return value;
}

// ── Payment card numbers, on EVERY surface (SOC 2 R60, PCI-2) ──────────────
//
// The gift-card rule above masks every 12–19 digit run, so it is applied only
// where a gift-card number can appear. A payment card number (PAN) can appear
// anywhere a request body, an error message or a breadcrumb reaches — the POS
// native keyed fields sent `{ kind: 'Card', pan }` to `/payments/sale` — so it
// is masked on every route, every log line and every error report.
//
// What makes a run a PAN here, and not an id or a timestamp:
//
//   - 12–19 digits, optionally grouped by single spaces or dashes (ISO/IEC
//     7812 lengths; the same run shape as the gift-card rule);
//   - it passes the Luhn check, which every payment network's numbers do and
//     nine in ten arbitrary digit runs do not;
//   - it starts with 2–7: the major-industry identifiers of the networks a
//     restaurant takes (2/5 Mastercard, 3 Amex/JCB/Diners, 4 Visa, 6 Discover/
//     UnionPay, and the gift-card BIN 6278) plus 7 (fleet/petroleum). A leading
//     1 is left alone on purpose: epoch-millisecond timestamps and the POS's
//     16-digit standalone order ids start with 1 until 2033, and masking one in
//     ten of them would blind every log they appear in; 1 is UATP (airline
//     cards), which no OrderUp merchant accepts.
//
// It over-matches by design (a Luhn-valid 13-digit id starting with 4 is
// masked): hiding an id costs a little debugging, a card number in a
// third-party log cannot be taken back.

/** The Luhn (mod 10) check over a run of digits. */
export function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/** True when a run of bare digits reads as a payment card number (rule above). */
export function looksLikePaymentCardNumber(digits: string): boolean {
  if (digits.length < 12 || digits.length > 19) return false;
  const lead = digits.charCodeAt(0) - 48;
  if (lead < 2 || lead > 7) return false;
  return passesLuhn(digits);
}

/**
 * Mask every payment card number in `text` to `[card…1234]`. Total and
 * non-throwing, like {@link redactCardNumberRuns}; a non-string comes back
 * unchanged.
 */
export function redactPaymentCardNumbers<T>(text: T): T {
  if (typeof text !== 'string' || text.length < 12) return text;
  return text.replace(CARD_NUMBER_RUN, (whole: string, lead: string, run: string) => {
    const digits = run.replace(/[ -]/g, '');
    if (!looksLikePaymentCardNumber(digits)) return whole;
    return lead + maskedCardNumber(digits.slice(-4));
  }) as unknown as T;
}

/** True when `text` contains at least one payment card number. */
export function containsPaymentCardNumber(text: unknown): boolean {
  return typeof text === 'string' && redactPaymentCardNumbers(text) !== text;
}

/**
 * Mask every payment card number reachable from `value`, IN PLACE for objects
 * and arrays (a `beforeSend` mutates and returns its event). A NUMBER that
 * reads as a card number (a PAN sent as a JSON number) becomes the masked
 * string. Returns the redacted value.
 */
export function redactPaymentCardNumbersDeep<T>(value: T, depth = 0): T {
  if (depth > 12 || value === null || value === undefined) return value;
  if (typeof value === 'string') return redactPaymentCardNumbers(value);
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value) || value < 0) return value;
    const digits = String(value);
    return (looksLikePaymentCardNumber(digits) ? maskedCardNumber(digits.slice(-4)) : value) as unknown as T;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = redactPaymentCardNumbersDeep(value[i], depth + 1);
    return value;
  }
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    obj[key] = redactPaymentCardNumbersDeep(obj[key], depth + 1);
  }
  return value;
}

/**
 * A URL as it may be written to a log line or a trace: on a gift-card path
 * every 12–19 digit run is masked ({@link redactGiftCardUrl}); on every other
 * path, every payment card number.
 */
export function redactCardNumberUrl<T>(url: T): T {
  if (typeof url !== 'string') return url;
  return isGiftCardPath(url) ? redactCardNumberRuns(url) : redactPaymentCardNumbers(url);
}
